import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated } from '../../../features/auth/authSlice';
import { useAddFavoriteMutation, useDeleteFavoriteMutation } from '../../../features/api/apiSlice';
import Icon from '../../../components/AppIcon';
import { useToast } from '../../../components/ui/Toast';
import {
  getTransactionBadgeText,
  TRANSACTION_BADGE_STYLE,
  isLuxuryListing,
  isTakenListing,
} from '../../../lib/propertyLabels';
import { useCurrency } from '../../../features/currency/CurrencyContext';

const findParameter = (parameters, name) => {
  const param = parameters?.find((p) => p.name?.toLowerCase() === name.toLowerCase());
  return param ? param.value : null;
};

const PropertyCard = ({ property, isFavorite, onToggleFavorite, variant = 'grid' }) => {
  if (!property) return null;

  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [addFavorite] = useAddFavoriteMutation();
  const [deleteFavorite] = useDeleteFavoriteMutation();
  const addToast = useToast();
  const { formatPrice } = useCurrency();

  const {
    id,
    slug_id,
    title,
    price,
    address,
    city,
    property_type,
    title_image,
    parameters,
    post_created,
    category,
  } = property;

  const bedrooms = findParameter(parameters, 'bedrooms');
  const bathrooms = findParameter(parameters, 'bathrooms');
  const area = findParameter(parameters, 'area') || findParameter(parameters, 'sqft');
  const location = [city, address].filter(Boolean).join(' · ') || address || city || 'Uganda';
  const typeBadge = getTransactionBadgeText(property_type);

  const handleToggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof onToggleFavorite === 'function') {
      onToggleFavorite(id);
      return;
    }
    if (!isAuthenticated) {
      addToast('Please log in to save properties.', 'info');
      navigate('/login');
      return;
    }
    try {
      if (isFavorite) {
        await deleteFavorite(id).unwrap();
        addToast('Removed from saved properties.', 'info');
      } else {
        await addFavorite(id).unwrap();
        addToast('Property saved to your shortlist.', 'success');
      }
    } catch (error) {
      addToast('Could not update saved list. Try again.', 'error');
    }
  };

  const isList = variant === 'list';
  const badges = (
    <>
      <span
        className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold shadow-sm"
        style={TRANSACTION_BADGE_STYLE}
      >
        {typeBadge}
      </span>
      {isLuxuryListing(property) ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-400 text-slate-950">
          <Icon name="Gem" size={10} />
          Luxury
        </span>
      ) : null}
      {isTakenListing(property) ? (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-500 text-white">
          Taken
        </span>
      ) : null}
    </>
  );

  if (isList) {
    return (
      <article
        itemScope
        itemType="https://schema.org/Offer"
        className="group relative flex h-[120px] sm:h-[128px] bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-slate-300 hover:shadow-md transition-shadow duration-200"
      >
        <div className="relative w-[132px] sm:w-[168px] h-full flex-shrink-0 overflow-hidden bg-slate-100">
          <Link
            to={`/property/${slug_id}`}
            className="block w-full h-full"
            title={`View details for ${title}`}
            aria-label={`View details for ${title}`}
          >
            {title_image ? (
              <img
                src={title_image}
                alt={`${title} - ${location}`}
                className="w-full h-full object-cover object-center"
                itemProp="image"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-400">
                <Icon name="ImageOff" size={22} />
              </div>
            )}
          </Link>
          <div className="absolute top-2 left-2 flex flex-nowrap gap-1 overflow-hidden max-w-[calc(100%-8px)]">
            {badges}
          </div>
        </div>

        <div className="flex min-w-0 flex-1 items-stretch">
          <div className="flex min-w-0 flex-1 flex-col justify-between px-3.5 py-2.5 sm:px-4 sm:py-3">
            <div className="min-w-0">
              <div className="flex items-start justify-between gap-3">
                <p className="text-[15px] sm:text-base font-bold text-slate-900 tracking-tight truncate" itemProp="price">
                  {formatPrice(price, 'UGX')}
                </p>
                <button
                  type="button"
                  onClick={handleToggleFavorite}
                  className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center flex-shrink-0 hover:bg-slate-50"
                  aria-label="Save property"
                >
                  <Icon
                    name="Heart"
                    size={14}
                    className={isFavorite ? 'text-rose-500 fill-rose-500' : 'text-slate-500'}
                  />
                </button>
              </div>
              <h3 className="mt-0.5 text-sm font-semibold text-slate-900 leading-snug truncate group-hover:text-primary">
                <Link to={`/property/${slug_id}`} itemProp="name">
                  {title}
                </Link>
              </h3>
              <p className="mt-0.5 text-xs text-slate-500 flex items-center gap-1 min-w-0" itemProp="address">
                <Icon name="MapPin" size={12} className="flex-shrink-0 text-slate-400" />
                <span className="truncate">{location}</span>
              </p>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 text-xs font-medium text-slate-600">
                {bedrooms != null && bedrooms !== '' && (
                  <span className="inline-flex items-center gap-1">
                    <Icon name="BedDouble" size={13} className="text-slate-400" />
                    {bedrooms}
                  </span>
                )}
                {bathrooms != null && bathrooms !== '' && (
                  <span className="inline-flex items-center gap-1">
                    <Icon name="Bath" size={13} className="text-slate-400" />
                    {bathrooms}
                  </span>
                )}
                {area != null && area !== '' && (
                  <span className="inline-flex items-center gap-1 truncate">
                    <Icon name="Maximize2" size={13} className="text-slate-400" />
                    {area}
                  </span>
                )}
                {category?.category && (
                  <span className="hidden sm:inline text-slate-400 font-normal truncate">{category.category}</span>
                )}
              </div>
              {post_created && <span className="text-[11px] text-slate-400 flex-shrink-0">{post_created}</span>}
            </div>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article
      itemScope
      itemType="https://schema.org/Offer"
      className="group relative flex flex-col bg-white rounded-2xl border border-slate-200/80 overflow-hidden shadow-sm hover:shadow-xl hover:border-slate-300 transition-all duration-300 hover:-translate-y-0.5"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        <Link
          to={`/property/${slug_id}`}
          className="block h-full"
          title={`View details for ${title}`}
          aria-label={`View details for ${title}`}
        >
          {title_image ? (
            <img
              src={title_image}
              alt={`${title} - ${location}`}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              itemProp="image"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-400">
              <Icon name="ImageOff" size={32} />
            </div>
          )}
        </Link>

        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />

        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {badges}
          {category?.category && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-slate-900/80 text-white shadow-sm">
              {category.category}
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={handleToggleFavorite}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 shadow-sm flex items-center justify-center hover:bg-white transition-colors"
          aria-label="Save property"
        >
          <Icon
            name="Heart"
            size={15}
            className={isFavorite ? 'text-rose-500 fill-rose-500' : 'text-slate-600'}
          />
        </button>
      </div>

      <div className="flex flex-col flex-1 p-4">
        <p className="text-lg font-bold text-slate-900 tracking-tight" itemProp="price">
          {formatPrice(price, 'UGX')}
        </p>
        <h3 className="mt-1.5 text-[15px] font-semibold text-slate-900 leading-snug line-clamp-2 group-hover:text-primary transition-colors">
          <Link to={`/property/${slug_id}`} itemProp="name">
            {title}
          </Link>
        </h3>
        <p className="mt-1.5 text-sm text-slate-500 flex items-start gap-1 line-clamp-1" itemProp="address">
          <Icon name="MapPin" size={14} className="mt-0.5 flex-shrink-0 text-slate-400" />
          <span className="truncate">{location}</span>
        </p>

        <div className="mt-auto pt-4 flex items-center justify-between border-t border-slate-100">
          <div className="flex items-center gap-3 text-xs font-medium text-slate-600">
            {bedrooms != null && bedrooms !== '' && (
              <span className="inline-flex items-center gap-1">
                <Icon name="BedDouble" size={14} className="text-slate-400" />
                {bedrooms}
              </span>
            )}
            {bathrooms != null && bathrooms !== '' && (
              <span className="inline-flex items-center gap-1">
                <Icon name="Bath" size={14} className="text-slate-400" />
                {bathrooms}
              </span>
            )}
            {area != null && area !== '' && (
              <span className="inline-flex items-center gap-1">
                <Icon name="Maximize2" size={14} className="text-slate-400" />
                {area}
              </span>
            )}
          </div>
          {post_created && <span className="text-[11px] text-slate-400">{post_created}</span>}
        </div>
      </div>
    </article>
  );
};

export default PropertyCard;
